import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/common/PageHeader";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { FileUp, Video, ClipboardList, Plane, HeartPulse, ArrowRight, Globe2 } from "lucide-react";

const journey = [
  { Icon: FileUp, title: "Share your reports", text: "Send your dental history, X-rays, or CBCT scans securely for an initial review." },
  { Icon: Video, title: "Remote consultation", text: "Meet our team over a video call to discuss your concerns and possibilities." },
  { Icon: ClipboardList, title: "Transparent plan & estimate", text: "Receive a clear treatment plan, expected number of visits, and honest cost estimate before you travel." },
  { Icon: Plane, title: "Travel & in-clinic care", text: "We help you plan your visit and coordinate treatment efficiently — often in fewer visits thanks to digital workflows." },
  { Icon: HeartPulse, title: "Aftercare & remote follow-up", text: "Return home with a clear aftercare plan and remote follow-up support." },
];

export default function InternationalPatients() {
  return (
    <>
      <Seo title="International Patients" description="Remote consultation, transparent planning, and travel guidance for NRIs and international patients at CDT." path="/international-patients" />
      <PageHeader eyebrow="For NRIs & International Patients" title="Care that travels with you" subtitle="Distance shouldn't stand between you and thoughtful dentistry. We make planning treatment from abroad clear, transparent, and reassuring." image="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=1200&q=80" />

      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Your Journey" title="A clear path, from anywhere" subtitle="Five simple steps designed around remote planning and efficient in-clinic visits." />
          <div className="mt-12 space-y-5">
            {journey.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.05}>
                <div className="flex gap-5 rounded-[var(--cdt-radius-md)] bg-white border border-[color:var(--cdt-border)] shadow-[var(--cdt-shadow-sm)] p-6">
                  <div className="flex flex-col items-center">
                    <span className="h-11 w-11 rounded-full bg-[rgba(15,92,90,0.08)] flex items-center justify-center"><s.Icon className="h-5 w-5 text-[var(--cdt-primary)]" /></span>
                    {i < journey.length - 1 && <span className="flex-1 w-px bg-[color:var(--cdt-border)] mt-2" />}
                  </div>
                  <div className="pb-1">
                    <div className="flex items-center gap-2"><span className="text-xs font-semibold text-[var(--cdt-accent)]">STEP {i + 1}</span></div>
                    <h3 className="font-display text-xl mt-1 text-[var(--cdt-text)]">{s.title}</h3>
                    <p className="mt-1.5 text-sm leading-7 text-[var(--cdt-muted)]">{s.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="rounded-[var(--cdt-radius-md)] bg-[var(--cdt-surface-2)] border border-[color:var(--cdt-border)] p-6 text-sm leading-7 text-[var(--cdt-muted)]">
              <p><span className="font-semibold text-[var(--cdt-text)]">A note on accommodation &amp; travel:</span> We are happy to share general, non-affiliated guidance on nearby stays and reaching the clinic. We never take commissions — our recommendations are made only in your interest.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-[var(--cdt-radius-lg)] bg-[var(--cdt-primary)] p-10 text-center">
            <Globe2 className="h-8 w-8 text-white/90 mx-auto" />
            <h2 className="font-display text-3xl text-white mt-3">Book an international consultation</h2>
            <p className="mt-3 text-white/80 max-w-xl mx-auto leading-7">Start with a remote consultation. Share your details and we'll arrange a convenient time across your time zone.</p>
            <Link to="/appointment?type=international" data-testid="international-consult-button" className="mt-7 inline-flex items-center gap-2 h-12 px-6 rounded-[var(--cdt-radius-sm)] bg-white text-[var(--cdt-primary-2)] font-semibold hover:bg-[var(--cdt-surface-2)] transition-colors">Request Consultation <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
